import type { Locale } from 'date-fns';
import {
	addDays,
	addMonths,
	eachDayOfInterval,
	endOfMonth,
	endOfWeek,
	format,
	getISOWeek,
	isDate,
	isSameDay as isSameDayFns,
	parseISO,
	startOfDay,
	startOfMonth,
	startOfWeek,
} from 'date-fns';

import { DateRange as DateRangeForApi, RelativeDate, RelativeDateStart } from '@/api';

export const DATE_FORMATS = {
	display: 'MMM dd, yyyy',
	displayWithTime: 'dd MMM yyyy HH:mm',
	iso: 'yyyy-MM-dd',
	long: 'MMMM dd, yyyy',
	monthYear: 'MMMM yyyy',
	monthDayYear: 'dd MMM yyyy',
	longDayName: 'EEEE',
	shortDayName: 'EEE',
	monthNameShort: 'MMM',
	fullDateWithDayNameAndYear: 'EEEE, d MMM yyyy',
} as const;

type DateInput = Date | string | null | undefined;

export function formatDateRange(start: Date, end: Date, locale?: Locale): string {
	const opts = locale ? { locale } : undefined;
	const startStr = format(start, 'MMM d', opts);
	const endStr = format(end, 'MMM d, yyyy', opts);

	return `${startStr} - ${endStr}`;
}

export function getDaysInRange(start: Date, end: Date): Date[] {
	const days = eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) });

	return days.reverse();
}

export function isSameDay(date1: Date, date2: Date): boolean {
	return isSameDayFns(date1, date2);
}

export function formatDateInput(date: Date): string {
	return format(date, 'yyyy-MM-dd');
}

export function getISOWeekNumber(date: Date): number {
	return getISOWeek(date);
}

export function getWeekStart(date: Date): Date {
	return startOfWeek(date, { weekStartsOn: 1 }); // Monday
}

export function getWeekEnd(date: Date): Date {
	return endOfWeek(date, { weekStartsOn: 1 }); // Sunday
}

export function formatMonthYear(date: Date, locale?: Locale): string {
	return format(date, 'MMMM yyyy', locale ? { locale } : undefined);
}

export function formatDate(date: DateInput, formatKey: keyof typeof DATE_FORMATS = 'display', locale?: Locale): string {
	if (!date) return '';

	return format(date, DATE_FORMATS[formatKey], locale ? { locale } : undefined);
}

export function formatDateDisplay(date: DateInput, locale?: Locale): string {
	return formatDate(date, 'display', locale);
}

export function formatDateLong(date: DateInput, locale?: Locale): string {
	return formatDate(date, 'long', locale);
}

export function formatDateWithTime(date: DateInput, locale?: Locale): string {
	return formatDate(date, 'displayWithTime', locale);
}

export function formatDateMonthDayYear(date: DateInput, locale?: Locale): string {
	return formatDate(date, 'monthDayYear', locale);
}

/**
 * Converts Date objects to API-compatible date strings (yyyy-MM-dd format).
 * API requires dates without time information.
 *
 * @example
 * formatDateRangeForApi({
 *   startDate: new Date('2024-01-15T14:30:00'),
 *   endDate: new Date('2024-01-20T09:45:00')
 * })
 * // Returns: { startDate: '2024-01-15', endDate: '2024-01-20' }
 */
export function formatDateForApi(date: Date | string): string {
	return formatDate(date, 'iso');
}

/**
 * Parses an API date string. Date-only values ('yyyy-MM-dd') become local midnight,
 * unlike `new Date('yyyy-MM-dd')` which is UTC midnight and shifts the day west of UTC.
 */
export function parseDateFromApi(date: string | Date): Date {
	if (isDate(date)) return date;

	return parseISO(date);
}

/**
 * Converts Date objects to API-compatible date strings (yyyy-MM-dd format).
 * API requires dates without time information.
 *
 * @example
 * formatDateRangeForApi({
 *   startDate: new Date('2024-01-15T14:30:00'),
 *   endDate: new Date('2024-01-20T09:45:00')
 * })
 * // Returns: { startDate: '2024-01-15', endDate: '2024-01-20' }
 */
export function formatDateRangeForApi(range: { startDate: Date | string; endDate: Date | string }): DateRangeForApi {
	return {
		startDate: formatDateForApi(range.startDate),
		endDate: formatDateForApi(range.endDate),
	};
}

export type DateRangeWithDates = {
	startDate: Date;
	endDate: Date;
};

export type OptionalDateRangeWithDates = {
	startDate: Date | null;
	endDate: Date | null;
};

/**
 * Converts API date range (string dates) to schema format (Date objects)
 */
export function parseDateRangeFromApi(range: { startDate: string; endDate: string }): DateRangeWithDates {
	return {
		startDate: parseDateFromApi(range.startDate),
		endDate: parseDateFromApi(range.endDate),
	};
}

/**
 * Returns the first and last day of the current month (using date-fns startOfMonth/endOfMonth).
 */
export function getCurrentMonthRange(): DateRangeWithDates {
	const now = new Date();

	return {
		startDate: startOfMonth(now),
		endDate: endOfMonth(now),
	};
}

/**
 * Calculates a date based on a RelativeDate configuration.
 * The calculation starts from the base date, start of month, or end of month (based on startFrom),
 * then adds the specified months and days.
 */
export function applyRelativeDateRules(baseDate: Date | null, calculation: RelativeDate | null): Date | null {
	if (!baseDate || !calculation) return null;

	let result: Date;

	switch (calculation.startFrom) {
		case RelativeDateStart.START_OF_BASE_MONTH:
			result = startOfMonth(baseDate);
			break;
		case RelativeDateStart.END_OF_BASE_MONTH:
			result = endOfMonth(baseDate);
			break;
		case RelativeDateStart.BASE_DATE:
		default:
			result = baseDate;
			break;
	}

	if (calculation.addMonths) {
		result = addMonths(result, calculation.addMonths);
	}

	if (calculation.addDays) {
		result = addDays(result, calculation.addDays);
	}

	return result;
}

/** Inclusive ISO date string range check. All three params must be 'yyyy-MM-dd' strings. */
export function isDateStringInRange(date: string, startDate: string, endDate: string): boolean {
	return date >= startDate && date <= endDate;
}
